import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { SignalMode } from '@constants/administration/machine/signal-mode.enum';
import { DATA_TYPE } from '@constants/data-type.constant';
import { HTTPS_REQUEST_OPTIONS } from '@constants/https-request-options/https-request-options';
import { StatusSignal } from '@models/adminstration/machine/status-signal/status-signal';
import { Signal } from '@models/dashboard/signal';
import { StatusEventSignalService } from '@services/administration/machine/status-event/status-event-signal.service';
import { ApiLocationService } from '@services/api-location/api-location.service';
import { Observable } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class SignalService {

  private SIGNALS_CONFIGURATION_SIGNAL_URL_SUFFIX = '/api/web/admin/machine/:id/signalsConfiguration?signal=:signal';
  private SIGNALS_CONFIGURATION_DELETE_SIGNAL_URL_SUFFIX = '/api/web/admin/machine/:id/signalsConfiguration?signalId=:signalId';
  private SIGNALS_CONFIGURATION_GET_STATUS_EVENT_SIGNAL_URL_SUFFIX = '/api/web/admin/machine/:id/signalsConfiguration/:signalId/statusEventDetails';

  private STATUS_SIGNALS_CONFIGURATION_URL_SUFFIX = '/api/web/admin/machine/:id/signalsConfiguration/statusSignal';
  private STATUS_SIGNALS_CONFIGURATION_GET_URL_SUFFIX = this.STATUS_SIGNALS_CONFIGURATION_URL_SUFFIX + '?signalId=:signalId';
  private STATUS_SIGNALS_CONFIGURATION_DELETE_URL_SUFFIX = this.STATUS_SIGNALS_CONFIGURATION_URL_SUFFIX + '?signalId=:signalId';


  private LIST_TABLES_FOR_MACHINE_URL_SUFFIX = '/api/web/admin/machine/:id/table';
  private LIST_SIGNALS_FOR_TABLE_URL_SUFFIX = '/api/web/admin/machine/:id/table/:name/signal';
  private LIST_CONSTANTS_FOR_MACHINE_URL_SUFFIX = '/api/web/admin/tenant/:id/constants/validNumericConstants';

  private GET_USED_SIGNALS_URL_SUFFIX = '/api/web/admin/machine/:id/usedSignal/:signalId';


  private readonly BASE_URL: string;

  constructor(private http: HttpClient,
              private apiLocation: ApiLocationService,
              private statusEventSignalService: StatusEventSignalService) {
    this.BASE_URL = this.apiLocation.getLocation();
  }

  private static makeObject(signal: StatusSignal) {
    const obj = {};
    for (const [key, value] of signal.statusMap) {
      obj[key] = value;
    }
    return obj;
  }

  private static getRequestForCalculatedSignal(signal: Signal) {
    return {
      calculated: signal.calculated,
      decimals: signal.decimals,
      formula: signal.formula,
      id: signal.id,
      isByteArray: signal.isByteArray,
      isStatusSignal: signal.isStatusSignal,
      isTriggerSignal: signal.isTriggerSignal,
      label: signal.label,
      name: signal.name,
      parentId: signal.parentId,
      parent: signal.parent,
      protect: signal.protect,
      scale: signal.scale,
      signal: signal.signal,
      signalColor: signal.signalColor,
      signalUsed: signal.signalUsed,
      signals: SignalService.getSignalIds(signal.signals),
      streamToBigQuery: signal.streamToBigQuery,
      table: signal.table,
      tenantConstants: SignalService.getTenantConstantIds(signal.tenantConstants),
      type: DATA_TYPE.FLOAT,
      unit: signal.unit,
      mode: SignalMode.NULLABLE
    };
  }

  private static getSignalIds(signals: any) {
    if (!signals || signals.length <= 0) {
      return new Array<any>();
    }
    const signalIds: number[] = new Array<number>();
    for (const signal of signals) {
      signalIds.push(signal.id);
    }
    return signalIds;
  }

  private static getTenantConstantIds(constants: any) {
    if (!constants || constants.length <= 0) {
      return new Array<any>();
    }
    const constantIds: number[] = new Array<number>();
    for (const constant of constants) {
      constantIds.push(constant.id);
    }
    return constantIds;
  }

  public getStatusSignal(machineId: number | undefined, signalId: number): Observable<any> {
    let url: string = this.BASE_URL + this.STATUS_SIGNALS_CONFIGURATION_GET_URL_SUFFIX;
    url = url.replace(':id', String(machineId));
    url = url.replace(':signalId', String(signalId));
    return this.http.get(url, HTTPS_REQUEST_OPTIONS);
  }

  public getStatusEventSignalDetails(machineId: number, signalId: number): Observable<any> {
    let url: string = this.BASE_URL + this.SIGNALS_CONFIGURATION_GET_STATUS_EVENT_SIGNAL_URL_SUFFIX;
    url = url.replace(':id', String(machineId));
    url = url.replace(':signalId', String(signalId));
    return this.http.get(url, HTTPS_REQUEST_OPTIONS);
  }

  public deleteStatusSignal(machineId: number | undefined, signalId: number): Observable<any> {
    let url: string = this.BASE_URL + this.STATUS_SIGNALS_CONFIGURATION_DELETE_URL_SUFFIX;
    url = url.replace(':id', String(machineId));
    url = url.replace(':signalId', String(signalId));
    return this.http.delete(url, HTTPS_REQUEST_OPTIONS);
  }

  public saveStatusSignal(signal: StatusSignal, machineId: number | undefined): Observable<any> {
    let url: string = this.BASE_URL + this.STATUS_SIGNALS_CONFIGURATION_URL_SUFFIX;
    url = url.replace(':id', String(machineId));
    const request = {
      id: signal.id,
      label: signal.label,
      machineId: signal.machineId,
      name: signal.name,
      parentId: signal.parentId,
      statusMap: SignalService.makeObject(signal),
    };
    return this.http.post(url, request, HTTPS_REQUEST_OPTIONS);
  }

  public deleteSignal(machineId: number | undefined, signalId: number): Observable<any> {
    let url: string = this.BASE_URL + this.SIGNALS_CONFIGURATION_DELETE_SIGNAL_URL_SUFFIX;
    url = url.replace(':id', String(machineId));
    url = url.replace(':signalId', String(signalId));
    return this.http.delete(url, HTTPS_REQUEST_OPTIONS);
  }

  public saveSignal(signal: Signal): Observable<any> {
    let url: string = this.BASE_URL + this.SIGNALS_CONFIGURATION_SIGNAL_URL_SUFFIX;
    url = url.replace(':id', String(signal.id));

    let request;
    if (signal.calculated) {
      url = url.replace(':signal', signal.name);
      request = SignalService.getRequestForCalculatedSignal(signal);
    } else {
      url = url.replace(':signal', signal.signal);
      request = this.getRequestForUncalculatedSignal(signal);
    }
    return this.http.post(url, request, HTTPS_REQUEST_OPTIONS);
  }

  public listTablesForMachine(machineId: number): Observable<any> {
    let url: string = this.BASE_URL + this.LIST_TABLES_FOR_MACHINE_URL_SUFFIX;
    url = url.replace(':id', String(machineId));
    return this.http.get(url, HTTPS_REQUEST_OPTIONS);
  }

  public listSignalsForTable(machineId: number, tableName: string): Observable<any> {
    let url: string = this.BASE_URL + this.LIST_SIGNALS_FOR_TABLE_URL_SUFFIX;
    url = url.replace(':id', String(machineId));
    url = url.replace(':name', String(tableName));
    return this.http.get(url, HTTPS_REQUEST_OPTIONS);
  }

  public listConstants(machineId: number): Observable<any> {
    let url: string = this.BASE_URL + this.LIST_CONSTANTS_FOR_MACHINE_URL_SUFFIX;
    url = url.replace(':id', String(machineId));
    return this.http.get(url, HTTPS_REQUEST_OPTIONS);
  }

  public getUsageInformationOfSignal(machineId: number, signalId: number): Observable<any> {
    let url: string = this.BASE_URL + this.GET_USED_SIGNALS_URL_SUFFIX;
    url = url.replace(':id', String(machineId))
    .replace(':signalId', String(signalId));
    return this.http.get(url, HTTPS_REQUEST_OPTIONS);
  }

  public signalLabelAlreadyUsed(allSignals: Signal[], updatedSignal: Signal): boolean {
    return allSignals.some(signal => signal.id !== updatedSignal.id && signal.label.toLowerCase() === updatedSignal.label.toLowerCase());
  }

  private getRequestForUncalculatedSignal(signal: Signal) {
    return {
      decimals: signal.decimals,
      id: signal.id,
      isByteArray: signal.isByteArray,
      isErrorWord: signal.isErrorWord,
      isStatusSignal: signal.isStatusSignal ? signal.isStatusSignal : false,
      isTriggerSignal: signal.isTriggerSignal,
      label: signal.label,
      mode: signal.mode,
      name: signal.name,
      parentId: signal.parentId,
      protect: signal.protect,
      scale: signal.scale,
      signal: signal.signal,
      signalColor: signal.signalColor,
      signalUsed: signal.signalUsed,
      table: signal.table,
      type: signal.type,
      unit: signal.unit,
      minWritableValue: signal.minWritableValue ?? null,
      maxWritableValue: signal.maxWritableValue ?? null,
      writable: signal.writable ?? false,
      statusEventsDetails: this.statusEventSignalService.convertStatusEventDetailsToObject(signal.statusEventsDetails)
    };
  }



}
